import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Field, inputCls } from "@/components/AuthShell";
import { api, SUBJECTS } from "@/lib/api";
import { useLang } from "@/context/LangContext";

export const AssignDialog = ({ open, onOpenChange, students, onSubmit, saving, levels = [] }) => {
  const { t, dir } = useLang();
  const tt = t.teacher;
  const [books, setBooks] = useState([]);
  const [subject, setSubject] = useState("all");
  const [bookId, setBookId] = useState("");
  const [whole, setWhole] = useState(true);
  const [ids, setIds] = useState([]);
  const [note, setNote] = useState("");
  const [due, setDue] = useState("");

  useEffect(() => {
    if (!open) return;
    setBookId(""); setWhole(true); setIds([]); setNote(""); setDue(""); setSubject("all");
    api.get("/books").then((r) => setBooks(Array.isArray(r.data) ? r.data : [])).catch(() => setBooks([]));
  }, [open]);

  const shown = books.filter((b) => (subject === "all" || b.subject === subject) && (!levels.length || levels.includes(b.level)));
  const toggle = (id) => setIds((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const valid = bookId && (whole || ids.length);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#FAF7F2] max-w-xl max-h-[90vh] overflow-y-auto" dir={dir} data-testid="teacher-assign-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-3xl text-start">{tt.dTitle}</DialogTitle>
        </DialogHeader>
        <form onSubmit={(e) => { e.preventDefault(); if (valid) onSubmit({ book_id: bookId, student_ids: whole ? [] : ids, note, due_date: due }); }} className="space-y-4" data-testid="teacher-assign-form">
          <div className="grid sm:grid-cols-3 gap-4">
            <Field label={tt.dSubject}>
              <select className={inputCls} value={subject} onChange={(e) => { setSubject(e.target.value); setBookId(""); }} data-testid="teacher-assign-subject">
                <option value="all">{tt.dAll}</option>
                {SUBJECTS.map((s) => <option key={s.id} value={s.id}>{t.subjects[s.id].name}</option>)}
              </select>
            </Field>
            <div className="sm:col-span-2">
              <Field label={tt.dBook}>
                <select required className={inputCls} value={bookId} onChange={(e) => setBookId(e.target.value)} data-testid="teacher-assign-book">
                  <option value="">{tt.dChoose}</option>
                  {shown.map((b) => <option key={b.id} value={b.id}>{b.title} · {b.level} {t.levels[b.level]}</option>)}
                </select>
              </Field>
            </div>
          </div>
          <div className="space-y-2">
            <span className="text-sm font-semibold">{tt.dWho}</span>
            <div className="grid grid-cols-2 gap-2">
              {[[true, tt.dWhole], [false, tt.dSome]].map(([v, l]) => (
                <button type="button" key={l} onClick={() => setWhole(v)} data-testid={`teacher-assign-target-${v ? "all" : "some"}`}
                  className={`h-11 rounded-full border text-sm font-semibold transition-colors ${whole === v ? "bg-[#1C1917] text-[#FAF7F2] border-[#1C1917]" : "bg-white border-[#E5DCCB] hover:border-[#1C1917]"}`}>{l}</button>
              ))}
            </div>
            {!whole && (
              <div className="max-h-48 overflow-y-auto bg-white border border-[#E5DCCB] divide-y divide-[#E5DCCB]" data-lenis-prevent>
                {students.map((s) => (
                  <label key={s.id} className="flex items-center gap-3 px-3 py-2.5 cursor-pointer hover:bg-[#FAF7F2]" data-testid={`teacher-assign-student-${s.id}`}>
                    <Checkbox checked={ids.includes(s.id)} onCheckedChange={() => toggle(s.id)} />
                    <span className="text-sm font-semibold">{s.name}</span>
                  </label>
                ))}
              </div>
            )}
          </div>
          <div className="grid sm:grid-cols-3 gap-4">
            <div className="sm:col-span-2"><Field label={tt.dNote}><input className={inputCls} value={note} onChange={(e) => setNote(e.target.value)} data-testid="teacher-assign-note" /></Field></div>
            <Field label={tt.dDue}><input type="date" className={inputCls} value={due} onChange={(e) => setDue(e.target.value)} data-testid="teacher-assign-due" /></Field>
          </div>
          <button disabled={saving || !valid} className="w-full h-12 rounded-full bg-[#1C1917] text-[#FAF7F2] font-semibold hover:bg-[#8B3A2B] transition-colors disabled:opacity-50" data-testid="teacher-assign-submit-btn">
            {saving ? tt.dSubmitting : tt.dSubmit}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
};
